/**
 * Advanced Webhook Management System
 * Handles webhook registration, delivery, security, and monitoring
 */

// Browser-compatible EventEmitter implementation
class EventEmitter {
  private events: { [key: string]: Function[] } = {};

  on(event: string, listener: Function): void {
    if (!this.events[event]) {
      this.events[event] = [];
    }
    this.events[event].push(listener);
  }

  off(event: string, listener: Function): void {
    if (!this.events[event]) return;
    this.events[event] = this.events[event].filter(l => l !== listener);
  }

  emit(event: string, ...args: any[]): void {
    if (!this.events[event]) return;
    this.events[event].forEach(listener => listener(...args));
  }
}

// Types for webhook management
export interface WebhookEndpoint {
  id: string;
  name: string;
  url: string;
  events: WebhookEvent[];
  secret: string;
  active: boolean;
  integrationId?: string;
  headers?: Record<string, string>;
  retryPolicy: WebhookRetryPolicy;
  filters?: WebhookFilter[];
  transformations?: WebhookTransformation[];
  createdAt: Date;
  updatedAt: Date;
  lastDelivery?: Date;
  deliveryStats: WebhookDeliveryStats;
}

export interface WebhookEvent {
  type: string;
  description: string;
  schema?: Record<string, any>;
}

export interface WebhookRetryPolicy {
  maxAttempts: number;
  backoffMultiplier: number;
  initialDelay: number; // milliseconds
  maxDelay: number; // milliseconds
  retryOn: number[]; // HTTP status codes to retry on
}

export interface WebhookFilter {
  id: string;
  field: string;
  operator: 'equals' | 'not_equals' | 'contains' | 'not_contains' | 'greater_than' | 'less_than';
  value: any;
  logicalOperator?: 'AND' | 'OR';
}

export interface WebhookTransformation {
  id: string;
  type: 'add_field' | 'remove_field' | 'rename_field' | 'transform_value' | 'custom_script';
  config: Record<string, any>;
}

export interface WebhookDelivery {
  id: string;
  webhookId: string;
  eventType: string;
  payload: any;
  status: 'pending' | 'delivered' | 'failed' | 'retrying';
  attempts: number;
  lastAttempt?: Date;
  nextRetry?: Date;
  responseStatus?: number;
  responseBody?: string;
  responseHeaders?: Record<string, string>;
  error?: string;
  deliveredAt?: Date;
  createdAt: Date;
}

export interface WebhookDeliveryStats {
  totalDeliveries: number;
  successfulDeliveries: number;
  failedDeliveries: number;
  averageResponseTime: number;
  lastSuccessfulDelivery?: Date;
  lastFailedDelivery?: Date;
  successRate: number;
}

export interface WebhookTestResult {
  success: boolean;
  responseStatus?: number;
  responseBody?: string;
  responseTime: number;
  error?: string;
}

export interface WebhookSecurityConfig {
  signatureHeader: string;
  signatureAlgorithm: 'sha256' | 'sha1' | 'md5';
  timestampHeader?: string;
  timestampTolerance?: number; // seconds
  ipWhitelist?: string[];
  requireHttps: boolean;
}

export class WebhookManager extends EventEmitter {
  private static instance: WebhookManager;
  private webhooks: Map<string, WebhookEndpoint> = new Map();
  private deliveries: Map<string, WebhookDelivery> = new Map();
  private deliveryQueue: WebhookDelivery[] = [];
  private processingInterval?: number;
  private securityConfig: WebhookSecurityConfig;

  private constructor() {
    super();
    this.securityConfig = {
      signatureHeader: 'X-Webhook-Signature',
      signatureAlgorithm: 'sha256',
      timestampHeader: 'X-Webhook-Timestamp',
      timestampTolerance: 300, // 5 minutes
      requireHttps: true
    };
    this.startDeliveryProcessor();
  }

  static getInstance(): WebhookManager {
    if (!WebhookManager.instance) {
      WebhookManager.instance = new WebhookManager();
    }
    return WebhookManager.instance;
  }

  /**
   * Register a new webhook endpoint
   */
  async registerWebhook(webhook: Partial<WebhookEndpoint>): Promise<string> {
    const webhookEndpoint: WebhookEndpoint = {
      id: webhook.id || this.generateId(),
      name: webhook.name!,
      url: webhook.url!,
      events: webhook.events || [],
      secret: webhook.secret || this.generateSecret(),
      active: webhook.active !== false,
      integrationId: webhook.integrationId,
      headers: webhook.headers || {},
      retryPolicy: webhook.retryPolicy || this.getDefaultRetryPolicy(),
      filters: webhook.filters || [],
      transformations: webhook.transformations || [],
      createdAt: webhook.createdAt || new Date(),
      updatedAt: new Date(),
      deliveryStats: webhook.deliveryStats || this.getInitialStats(),
      ...webhook
    };

    // Validate webhook URL
    if (!this.isValidUrl(webhookEndpoint.url)) {
      throw new Error('Invalid webhook URL');
    }

    // Validate HTTPS requirement
    if (this.securityConfig.requireHttps && !webhookEndpoint.url.startsWith('https://')) {
      throw new Error('Webhook URL must use HTTPS');
    }

    this.webhooks.set(webhookEndpoint.id, webhookEndpoint);
    this.emit('webhook_registered', webhookEndpoint);
    
    console.log(`Webhook registered: ${webhookEndpoint.name} (${webhookEndpoint.id})`);
    return webhookEndpoint.id;
  }

  /**
   * Update an existing webhook
   */
  async updateWebhook(webhookId: string, updates: Partial<WebhookEndpoint>): Promise<void> {
    const webhook = this.webhooks.get(webhookId);
    if (!webhook) {
      throw new Error(`Webhook not found: ${webhookId}`);
    }

    const updatedWebhook = {
      ...webhook,
      ...updates,
      updatedAt: new Date()
    };

    this.webhooks.set(webhookId, updatedWebhook);
    this.emit('webhook_updated', updatedWebhook);
  }

  /**
   * Delete a webhook
   */
  async deleteWebhook(webhookId: string): Promise<void> {
    const webhook = this.webhooks.get(webhookId);
    if (!webhook) {
      throw new Error(`Webhook not found: ${webhookId}`);
    }

    this.webhooks.delete(webhookId);
    this.emit('webhook_deleted', webhookId);
    console.log(`Webhook deleted: ${webhook.name} (${webhookId})`);
  }

  /**
   * Get all webhooks
   */
  getWebhooks(integrationId?: string): WebhookEndpoint[] {
    let webhooks = Array.from(this.webhooks.values());
    
    if (integrationId) {
      webhooks = webhooks.filter(w => w.integrationId === integrationId);
    }
    
    return webhooks.sort((a, b) => b.createdAt.getTime() - a.createdAt.getTime());
  }

  /**
   * Get a specific webhook
   */
  getWebhook(webhookId: string): WebhookEndpoint | undefined {
    return this.webhooks.get(webhookId);
  }

  /**
   * Trigger a webhook delivery
   */
  async triggerWebhook(eventType: string, payload: any, integrationId?: string): Promise<string[]> {
    const deliveryIds: string[] = [];
    
    // Find webhooks that should receive this event
    const eligibleWebhooks = Array.from(this.webhooks.values()).filter(webhook => {
      if (!webhook.active) return false;
      if (integrationId && webhook.integrationId !== integrationId) return false;
      if (!webhook.events.some(event => event.type === eventType)) return false;
      
      // Apply filters
      if (webhook.filters && webhook.filters.length > 0) {
        return this.applyFilters(payload, webhook.filters);
      }
      
      return true;
    });

    // Create deliveries for eligible webhooks
    for (const webhook of eligibleWebhooks) {
      const deliveryId = await this.createDelivery(webhook, eventType, payload);
      deliveryIds.push(deliveryId);
    }

    return deliveryIds;
  }

  /**
   * Test a webhook endpoint
   */
  async testWebhook(webhookId: string, testPayload?: any): Promise<WebhookTestResult> {
    const webhook = this.webhooks.get(webhookId);
    if (!webhook) {
      throw new Error(`Webhook not found: ${webhookId}`);
    }

    const payload = testPayload || {
      event: 'webhook.test',
      timestamp: new Date().toISOString(),
      data: { message: 'This is a test webhook delivery' }
    };

    const startTime = Date.now();
    
    try {
      const response = await this.deliverWebhook(webhook, payload);
      const responseTime = Date.now() - startTime;
      
      return {
        success: response.status >= 200 && response.status < 300,
        responseStatus: response.status,
        responseBody: response.body,
        responseTime
      };
    } catch (error) {
      const responseTime = Date.now() - startTime;
      return {
        success: false,
        responseTime,
        error: error instanceof Error ? error.message : 'Unknown error'
      };
    }
  }

  /**
   * Get webhook deliveries
   */
  getDeliveries(webhookId?: string, limit: number = 50): WebhookDelivery[] {
    let deliveries = Array.from(this.deliveries.values());
    
    if (webhookId) {
      deliveries = deliveries.filter(d => d.webhookId === webhookId);
    }
    
    return deliveries
      .sort((a, b) => b.createdAt.getTime() - a.createdAt.getTime())
      .slice(0, limit);
  }

  /**
   * Get delivery statistics
   */
  getDeliveryStats(webhookId?: string, timeRange?: { start: Date; end: Date }): WebhookDeliveryStats {
    let deliveries = Array.from(this.deliveries.values());
    
    if (webhookId) {
      deliveries = deliveries.filter(d => d.webhookId === webhookId);
    }
    
    if (timeRange) {
      deliveries = deliveries.filter(d => 
        d.createdAt >= timeRange.start && d.createdAt <= timeRange.end
      );
    }
    
    const totalDeliveries = deliveries.length;
    const successfulDeliveries = deliveries.filter(d => d.status === 'delivered').length;
    const failedDeliveries = deliveries.filter(d => d.status === 'failed').length;
    
    const successfulWithTiming = deliveries.filter(d => 
      d.status === 'delivered' && d.deliveredAt && d.createdAt
    );
    
    const averageResponseTime = successfulWithTiming.length > 0
      ? successfulWithTiming.reduce((sum, d) => 
          sum + (d.deliveredAt!.getTime() - d.createdAt.getTime()), 0
        ) / successfulWithTiming.length
      : 0;
    
    const lastSuccessfulDelivery = deliveries
      .filter(d => d.status === 'delivered')
      .sort((a, b) => (b.deliveredAt?.getTime() || 0) - (a.deliveredAt?.getTime() || 0))[0]?.deliveredAt;
    
    const lastFailedDelivery = deliveries
      .filter(d => d.status === 'failed')
      .sort((a, b) => (b.lastAttempt?.getTime() || 0) - (a.lastAttempt?.getTime() || 0))[0]?.lastAttempt;
    
    return {
      totalDeliveries,
      successfulDeliveries,
      failedDeliveries,
      averageResponseTime,
      lastSuccessfulDelivery,
      lastFailedDelivery,
      successRate: totalDeliveries > 0 ? (successfulDeliveries / totalDeliveries) * 100 : 0
    };
  }

  /**
   * Verify webhook signature
   */
  verifySignature(payload: string, signature: string, secret: string): boolean {
    const expectedSignature = this.generateSignature(payload, secret);
    // Simple string comparison for demo - in production, use timing-safe comparison
    return signature === expectedSignature;
  }

  /**
   * Generate webhook signature
   */
  generateSignature(payload: string, secret: string): string {
    // For demo purposes, we'll use a simple hash
    // In production, use Web Crypto API or a proper HMAC library
    const combined = payload + secret;
    let hash = 0;
    for (let i = 0; i < combined.length; i++) {
      const char = combined.charCodeAt(i);
      hash = ((hash << 5) - hash) + char;
      hash = hash & hash; // Convert to 32-bit integer
    }
    return `${this.securityConfig.signatureAlgorithm}=${Math.abs(hash).toString(16)}`;
  }

  /**
   * Create a delivery record
   */
  private async createDelivery(webhook: WebhookEndpoint, eventType: string, payload: any): Promise<string> {
    // Apply transformations
    const transformedPayload = this.applyTransformations(payload, webhook.transformations || []);
    
    const delivery: WebhookDelivery = {
      id: this.generateId(),
      webhookId: webhook.id,
      eventType,
      payload: transformedPayload,
      status: 'pending',
      attempts: 0,
      createdAt: new Date()
    };

    this.deliveries.set(delivery.id, delivery);
    this.deliveryQueue.push(delivery);
    
    this.emit('delivery_created', delivery);
    return delivery.id;
  }

  /**
   * Process delivery queue
   */
  private startDeliveryProcessor(): void {
    this.processingInterval = setInterval(async () => {
      await this.processDeliveryQueue();
    }, 1000) as any; // Process every second
  }

  /**
   * Process pending deliveries
   */
  private async processDeliveryQueue(): Promise<void> {
    const now = new Date();
    
    // Find deliveries ready for processing
    const readyDeliveries = this.deliveryQueue.filter(delivery => {
      if (delivery.status === 'delivered') return false;
      if (delivery.nextRetry && delivery.nextRetry > now) return false;
      
      const webhook = this.webhooks.get(delivery.webhookId);
      if (!webhook || !webhook.active) return false;
      
      return delivery.attempts < webhook.retryPolicy.maxAttempts;
    });

    // Process deliveries
    for (const delivery of readyDeliveries) {
      await this.processDelivery(delivery);
    }

    // Clean up completed deliveries from queue
    this.deliveryQueue = this.deliveryQueue.filter(d => 
      d.status === 'pending' || d.status === 'retrying'
    );
  }

  /**
   * Process a single delivery
   */
  private async processDelivery(delivery: WebhookDelivery): Promise<void> {
    const webhook = this.webhooks.get(delivery.webhookId);
    if (!webhook) return;

    delivery.attempts++;
    delivery.lastAttempt = new Date();
    delivery.status = 'retrying';

    try {
      const response = await this.deliverWebhook(webhook, delivery.payload);
      
      if (response.status >= 200 && response.status < 300) {
        // Success
        delivery.status = 'delivered';
        delivery.deliveredAt = new Date();
        delivery.responseStatus = response.status;
        delivery.responseBody = response.body;
        delivery.responseHeaders = response.headers;
        
        // Update webhook stats
        this.updateWebhookStats(webhook.id, true);
        
        this.emit('delivery_succeeded', delivery);
      } else {
        // HTTP error
        throw new Error(`HTTP ${response.status}: ${response.body}`);
      }
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error';
      delivery.error = errorMessage;
      
      if (delivery.attempts >= webhook.retryPolicy.maxAttempts) {
        // Max attempts reached
        delivery.status = 'failed';
        this.updateWebhookStats(webhook.id, false);
        this.emit('delivery_failed', delivery);
      } else {
        // Schedule retry
        const delay = this.calculateRetryDelay(delivery.attempts, webhook.retryPolicy);
        delivery.nextRetry = new Date(Date.now() + delay);
        delivery.status = 'retrying';
        this.emit('delivery_retry_scheduled', delivery);
      }
    }

    this.deliveries.set(delivery.id, delivery);
  }

  /**
   * Deliver webhook to endpoint
   */
  private async deliverWebhook(webhook: WebhookEndpoint, payload: any): Promise<{
    status: number;
    body: string;
    headers: Record<string, string>;
  }> {
    const payloadString = JSON.stringify(payload);
    const signature = this.generateSignature(payloadString, webhook.secret);
    const timestamp = Math.floor(Date.now() / 1000).toString();

    const headers = {
      'Content-Type': 'application/json',
      [this.securityConfig.signatureHeader]: signature,
      ...(this.securityConfig.timestampHeader && {
        [this.securityConfig.timestampHeader]: timestamp
      }),
      ...webhook.headers
    };

    // In a real implementation, this would use fetch or axios
    // For now, we'll simulate the HTTP request
    return this.simulateHttpRequest(webhook.url, {
      method: 'POST',
      headers,
      body: payloadString
    });
  }

  /**
   * Simulate HTTP request (replace with real HTTP client in production)
   */
  private async simulateHttpRequest(_url: string, _options: any): Promise<{
    status: number;
    body: string;
    headers: Record<string, string>;
  }> {
    // Simulate network delay
    await new Promise(resolve => setTimeout(resolve, Math.random() * 1000 + 100));
    
    // Simulate different response scenarios
    const random = Math.random();
    
    if (random < 0.85) {
      // Success (85% of the time)
      return {
        status: 200,
        body: JSON.stringify({ success: true, message: 'Webhook received' }),
        headers: { 'content-type': 'application/json' }
      };
    } else if (random < 0.95) {
      // Temporary error (10% of the time)
      return {
        status: 503,
        body: JSON.stringify({ error: 'Service temporarily unavailable' }),
        headers: { 'content-type': 'application/json' }
      };
    } else {
      // Permanent error (5% of the time)
      return {
        status: 400,
        body: JSON.stringify({ error: 'Bad request' }),
        headers: { 'content-type': 'application/json' }
      };
    }
  }

  /**
   * Apply filters to payload
   */
  private applyFilters(payload: any, filters: WebhookFilter[]): boolean {
    if (filters.length === 0) return true;
    
    let result = true;
    let currentLogicalOp: 'AND' | 'OR' = 'AND';
    
    for (const filter of filters) {
      const fieldValue = this.getNestedValue(payload, filter.field);
      const filterResult = this.evaluateFilter(fieldValue, filter.operator, filter.value);
      
      if (currentLogicalOp === 'AND') {
        result = result && filterResult;
      } else {
        result = result || filterResult;
      }
      
      currentLogicalOp = filter.logicalOperator || 'AND';
    }
    
    return result;
  }

  /**
   * Apply transformations to payload
   */
  private applyTransformations(payload: any, transformations: WebhookTransformation[]): any {
    let result = { ...payload };
    
    for (const transformation of transformations) {
      switch (transformation.type) {
        case 'add_field':
          result[transformation.config.field] = transformation.config.value;
          break;
        case 'remove_field':
          delete result[transformation.config.field];
          break;
        case 'rename_field':
          if (result[transformation.config.oldField] !== undefined) {
            result[transformation.config.newField] = result[transformation.config.oldField];
            delete result[transformation.config.oldField];
          }
          break;
        // Add more transformation types as needed
      }
    }
    
    return result;
  }

  /**
   * Evaluate filter condition
   */
  private evaluateFilter(fieldValue: any, operator: string, filterValue: any): boolean {
    switch (operator) {
      case 'equals':
        return fieldValue === filterValue;
      case 'not_equals':
        return fieldValue !== filterValue;
      case 'contains':
        return String(fieldValue).includes(String(filterValue));
      case 'not_contains':
        return !String(fieldValue).includes(String(filterValue));
      case 'greater_than':
        return Number(fieldValue) > Number(filterValue);
      case 'less_than':
        return Number(fieldValue) < Number(filterValue);
      default:
        return true;
    }
  }

  /**
   * Get nested value from object
   */
  private getNestedValue(obj: any, path: string): any {
    return path.split('.').reduce((current, key) => current?.[key], obj);
  }

  /**
   * Calculate retry delay with exponential backoff
   */
  private calculateRetryDelay(attempt: number, retryPolicy: WebhookRetryPolicy): number {
    const delay = retryPolicy.initialDelay * Math.pow(retryPolicy.backoffMultiplier, attempt - 1);
    return Math.min(delay, retryPolicy.maxDelay);
  }

  /**
   * Update webhook delivery statistics
   */
  private updateWebhookStats(webhookId: string, success: boolean): void {
    const webhook = this.webhooks.get(webhookId);
    if (!webhook) return;

    webhook.deliveryStats.totalDeliveries++;
    
    if (success) {
      webhook.deliveryStats.successfulDeliveries++;
      webhook.deliveryStats.lastSuccessfulDelivery = new Date();
    } else {
      webhook.deliveryStats.failedDeliveries++;
      webhook.deliveryStats.lastFailedDelivery = new Date();
    }
    
    webhook.deliveryStats.successRate = 
      (webhook.deliveryStats.successfulDeliveries / webhook.deliveryStats.totalDeliveries) * 100;
    
    webhook.lastDelivery = new Date();
    this.webhooks.set(webhookId, webhook);
  }

  /**
   * Utility methods
   */
  private generateId(): string {
    return `webhook_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }

  private generateSecret(): string {
    // Generate a random secret using browser-compatible method
    const chars = 'abcdef0123456789';
    let result = '';
    for (let i = 0; i < 64; i++) { // 64 hex chars = 32 bytes
      result += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    return result;
  }

  private isValidUrl(url: string): boolean {
    try {
      new URL(url);
      return true;
    } catch {
      return false;
    }
  }

  private getDefaultRetryPolicy(): WebhookRetryPolicy {
    return {
      maxAttempts: 5,
      backoffMultiplier: 2,
      initialDelay: 1000, // 1 second
      maxDelay: 300000, // 5 minutes
      retryOn: [408, 429, 500, 502, 503, 504]
    };
  }

  private getInitialStats(): WebhookDeliveryStats {
    return {
      totalDeliveries: 0,
      successfulDeliveries: 0,
      failedDeliveries: 0,
      averageResponseTime: 0,
      successRate: 0
    };
  }

  /**
   * Cleanup resources
   */
  destroy(): void {
    if (this.processingInterval) {
      clearInterval(this.processingInterval);
    }
  }
}

// Export singleton instance
export const webhookManager = WebhookManager.getInstance();